import { Button, IconButton, Paper, TextField, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Field, Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import GlobalWrapper from '../../../components/Wrapper/GlobalWrapper';
import { generalSingleUpload } from '../../../redux/features/generalSlice';
import { RegularizationPostThunk } from '../../../redux/features/leaveSlice';
import useHandleApiResponse from '../../../Hooks/useHandleApiResponse';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { RegularizationRequestValidationSchema } from './Regularize/RegularizationRequestValidation';

export default function RegularizationRequest() {
  const loading = useSelector((state) => state.leave.loading);
  const [file, setFile] = useState(null);
  const [view, setView] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const handleApiResponse = useHandleApiResponse();
  const navigate = useNavigate();
  const { state } = useLocation();

  const validationSchema = RegularizationRequestValidationSchema(state?.row?.leaveId);
  function validateFile(file) {
    const allowedTypes = ['image/png', 'image/jpeg', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      return 'Invalid file type. Only PNG, JPG, PDF allowed.';
    }

    if (file.size > maxSize) {
      return 'File too large. Maximum allowed is 10MB.';
    }
    return 'OK';
  }

  const UplaodHandler = (file, setFielValue) => {
    const formData = new FormData();
    formData.set('uploads', file);
    dispatch(generalSingleUpload(formData))
      .unwrap()
      .then(({ data }) => {
        enqueueSnackbar('file uploaded success', { variant: 'success' });

        setFielValue('supportingDocuments', [data.filename]);
        setView(data?.filename);
      })
      .catch(({ message }) => {
        setView('');
        enqueueSnackbar(message, { variant: 'error' });
      });
  };

  const submitHandler = (data) => {
    dispatch(RegularizationPostThunk(data)).then((res) => {
      handleApiResponse(res, {
        onSuccess: () => {
          navigate('/employeeservices/attendance-regularization');
        },
        onFailed: () => {
          console.log('faild');
        },
      });
    });
  };

  const initialValue = {
    reg_date: dayjs().format('YYYY-MM-DD'),
    reg_check_in: '',
    reg_check_out: '',
    fromDate: state?.row?.fromDate ?? '',
    toDate: state?.row?.toDate ?? '',
    leaveType: state?.row?.leaveType,
    noOfDays: state?.row?.noOfdays,
    reg_reason: '',
    supportingDocuments: [],
    leaveId: state?.row?.leaveId,
  };

  const flag = state?.row;

  return (
    <GlobalWrapper title={'New Regularization Request'}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            bgcolor: '#D9D9D9',
            p: 3,
            borderRadius: 5,
          }}
        >
          <Paper
            sx={{
              p: 2,
            }}
          >
            <Formik initialValues={initialValue} validationSchema={validationSchema} onSubmit={submitHandler}>
              {({ setFieldValue, values, errors, touched }) => (
                <Form>
                  <Stack
                    gap={5}
                    maxWidth={700}
                    sx={{
                      '& .css-ulrxro-MuiInputBase-root-MuiOutlinedInput-root': {
                        borderRadius: '45px !important',
                      },
                    }}
                  >
                    {!flag && (
                      <DatePicker
                        label={'Date'}
                        renderInput={(params) => <TextField {...params} />}
                        name="reg_date"
                        value={dayjs(values.reg_date)}
                        disableFuture={true}
                        minDate={dayjs().startOf('month')}
                        slotProps={{
                          textField: {
                            helperText: touched.reg_date && errors.reg_date,
                            error: touched.reg_date && errors.reg_date,
                          },
                        }}
                        onChange={(value) => setFieldValue('reg_date', dayjs(value).format('YYYY-MM-DD'))}
                      />
                    )}
                    <Box display={'flex'} gap={3} justifyContent={'space-between'}>
                      {flag ? (
                        <>
                          <DatePicker
                            label={'From Date'}
                            renderInput={(params) => <TextField {...params} />}
                            value={dayjs(values?.fromDate) ?? null}
                            disabled
                            disableFuture={true}
                          />
                          <DatePicker
                            label={'To Date'}
                            renderInput={(params) => <TextField {...params} />}
                            value={dayjs(values?.toDate) ?? null}
                            disabled
                            disableFuture={true}
                          />
                        </>
                      ) : (
                        <>
                          <TimePicker
                            label={'Check In'}
                            slotProps={{
                              textField: {
                                helperText: touched.reg_check_in && errors.reg_check_in,
                                error: touched.reg_check_in && errors.reg_check_in,
                              },
                            }}
                            onChange={(e) => setFieldValue('reg_check_in', dayjs(e).format('HH:mm'))}
                          />
                          <TimePicker
                            label={'Check Out'}
                            slotProps={{
                              textField: {
                                helperText: touched.reg_check_out && errors.reg_check_out,
                                error: touched.reg_check_out && errors.reg_check_out,
                              },
                            }}
                            onChange={(e) => setFieldValue('reg_check_out', dayjs(e).format('HH:mm'))}
                          />
                        </>
                      )}
                    </Box>
                    {flag && (
                      <Box display={'flex'} gap={3} justifyContent={'space-between'}>
                        <Field as={TextField} label="Type" name="leaveType" disabled />
                        <Field as={TextField} label="No.Days" name="noOfDays" disabled />
                      </Box>
                    )}{' '}
                    <TextField
                      label="Reason"
                      minRows={4}
                      maxRows={4}
                      multiline
                      helperText={touched.reg_reason && errors.reg_reason}
                      error={touched.reg_reason && errors.reg_reason}
                      sx={{
                        '& .MuiOutlinedInput-root fieldset': {
                          borderRadius: '30px',
                        },
                      }}
                      onChange={(value) => setFieldValue('reg_reason', value.target.value)}
                    />
                    <Box>
                      <Typography>Supporting Documents</Typography>
                      <label htmlFor="fileUpload">
                        <Box
                          sx={{
                            border: `2px dashed ${file ? '#2f72b3' : '#dce0e4'}`,
                            borderRadius: 7,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 200,
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            const result = validateFile(e.dataTransfer.files[0]);
                            if (result === 'OK') {
                              setFile(e.dataTransfer.files);
                              UplaodHandler(e.dataTransfer.files[0], setFieldValue);
                            } else
                              enqueueSnackbar(result, {
                                variant: 'error',
                              });
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                          }}
                        >
                          <input
                            type={'file'}
                            id="fileUpload"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                              const result = validateFile(e.target.files[0]);
                              if (result === 'OK') {
                                setFile(e.target.files);
                                UplaodHandler(e.target.files[0], setFieldValue);
                              } else
                                enqueueSnackbar(result, {
                                  variant: 'error',
                                });
                            }}
                          />
                          <Box>
                            {view ? (
                              <Box
                                sx={{
                                  bgcolor: '#dce0e4',
                                  p: 1,
                                  px: 2,
                                  borderRadius: 4,
                                  display: 'flex',
                                  gap: 0.5,
                                  alignItems: 'center',
                                }}
                              >
                                <Typography variant="body1">{file[0].name}</Typography>
                                <a
                                  href={`${process.env.REACT_APP_API_URL}uploads/${view}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <IconButton>
                                    <RemoveRedEyeIcon />
                                  </IconButton>
                                </a>
                              </Box>
                            ) : (
                              <Typography variant="body2">
                                <span
                                  style={{
                                    textDecoration: 'none',
                                    color: '#0295EB',
                                    cursor: 'pointer',
                                  }}
                                >
                                  Upload a file
                                </span>{' '}
                                or drag and drop
                                <br />
                                PNG,JPG,PDF up to 10MB
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </label>
                    </Box>
                    <Box display={'flex'} gap={2} alignSelf={'flex-end'}>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          navigate(-1);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button variant="contained" type="submit" disabled={loading}>
                        {loading ? 'Pleas Wait...' : 'Submit'}
                      </Button>
                    </Box>
                  </Stack>
                </Form>
              )}
            </Formik>
          </Paper>
        </Box>
      </LocalizationProvider>
    </GlobalWrapper>
  );
}
